import * as React from 'react';
import { IScoreboardBranding } from '../types';
import { UPLOAD_CONSTRAINTS } from '../config/constants';

const fileToBase64 = (file: File): Promise<string> => {
  return new Promise(
    (
      resolve: (value: string) => void,
      reject: (reason?: ProgressEvent<FileReader>) => void
    ): void => {
      const reader = new FileReader();
      reader.onload = (): void => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    }
  );
};

interface UseLogoUploadResult {
  uploadingLogo: boolean;
  uploadError: string | null;
  clearUploadError: () => void;
  handleLogoUpload: (
    event: React.ChangeEvent<HTMLInputElement>
  ) => Promise<void>;
}

export const useLogoUpload = (
  updateBranding: (updates: Partial<IScoreboardBranding>) => void
): UseLogoUploadResult => {
  const [uploadingLogo, setUploadingLogo] = React.useState<boolean>(false);
  const [uploadError, setUploadError] = React.useState<string | null>(null);

  const clearUploadError = React.useCallback((): void => {
    setUploadError(null);
  }, []);

  const handleLogoUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ): Promise<void> => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadError(null);

    // Validate file type
    if (
      !UPLOAD_CONSTRAINTS.ALLOWED_IMAGE_TYPES.includes(
        file.type as (typeof UPLOAD_CONSTRAINTS.ALLOWED_IMAGE_TYPES)[number]
      )
    ) {
      setUploadError(
        'Invalid file type. Please upload an image file (JPEG, PNG, GIF, SVG, or WebP).'
      );
      event.target.value = '';
      return;
    }

    // Validate file size
    if (file.size > UPLOAD_CONSTRAINTS.MAX_LOGO_SIZE_BYTES) {
      const maxSizeMB =
        UPLOAD_CONSTRAINTS.MAX_LOGO_SIZE_BYTES / (1024 * 1024);
      setUploadError(
        `File size exceeds ${maxSizeMB}MB limit. Please choose a smaller image.`
      );
      event.target.value = '';
      return;
    }

    setUploadingLogo(true);
    try {
      const base64Url = await fileToBase64(file);
      updateBranding({ logoUrl: base64Url, selectedLogoId: null });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error processing logo:', error);
      setUploadError('Failed to process logo. Please try again.');
    } finally {
      setUploadingLogo(false);
      event.target.value = '';
    }
  };

  return { uploadingLogo, uploadError, clearUploadError, handleLogoUpload };
};
